import { httpClient } from "@bw/shared/api";
import type {
  AlertAssignRequest,
  AlertListResponse,
  AlertMuteRequest,
  AlertResponse,
  Schema,
} from "@bw/shared/types/generated";

export type AdminRosterResponse = Schema["PlatformAdminAccessRosterResponse"];

/**
 * Server-side filters for GET /alerts. Only the fields actually supplied are
 * put on the wire — unset fields are omitted, never sent as empty/undefined
 * strings — so the backend applies exactly the operator's chosen narrowing.
 */
export interface AlertFilters {
  status?: AlertResponse["status"];
  severity?: AlertResponse["severity"];
  assigned_to?: AlertResponse["assigned_to"];
  limit?: number;
  offset?: number;
}

function toQueryString(filters: AlertFilters): string {
  const sp = new URLSearchParams();
  if (filters.status) sp.set("status", filters.status);
  if (filters.severity) sp.set("severity", filters.severity);
  if (filters.assigned_to) sp.set("assigned_to", filters.assigned_to);
  if (filters.limit !== undefined) sp.set("limit", String(filters.limit));
  if (filters.offset !== undefined) sp.set("offset", String(filters.offset));
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
}

/**
 * GET /alerts — the dedicated alerting list (NOT the old audit-derived read,
 * which was always empty in production). Returns the {items, total} envelope.
 */
export async function listAlerts(
  filters: AlertFilters = {},
): Promise<AlertListResponse> {
  return httpClient.get<AlertListResponse>(`/alerts${toQueryString(filters)}`);
}

/** GET /alerts/{id} — authoritative exact-alert read used after writes. */
export async function getAlert(alertId: string): Promise<AlertResponse> {
  return httpClient.get<AlertResponse>(
    `/alerts/${encodeURIComponent(alertId)}`,
  );
}

/**
 * GET /platform/admin-access/roster — the current platform-admins roster. Used
 * to populate the assignee picker (by display name) and to resolve an alert's
 * raw `assigned_to` uid back to a human display name.
 */
export async function listAdminRoster(): Promise<AdminRosterResponse> {
  return httpClient.get<AdminRosterResponse>("/platform/admin-access/roster");
}

/** POST /alerts/{id}/acknowledge — no body; the CSRF header is added by httpClient. */
export async function acknowledgeAlert(
  alertId: string,
): Promise<AlertResponse> {
  return httpClient.post<AlertResponse>(
    `/alerts/${encodeURIComponent(alertId)}/acknowledge`,
  );
}

/** POST /alerts/{id}/assign — AlertAssignRequest body {assignee_user_id}. */
export async function assignAlert(
  alertId: string,
  assigneeUserId: string,
): Promise<AlertResponse> {
  const request: AlertAssignRequest = {
    assignee_user_id: assigneeUserId,
  };
  return httpClient.post<AlertResponse>(
    `/alerts/${encodeURIComponent(alertId)}/assign`,
    request,
  );
}

/**
 * POST /alerts/{id}/mute — AlertMuteRequest body. Exactly one of {minutes}
 * (relative, > 0) or {muted_until} (absolute ISO) is supplied by the caller.
 */
export async function muteAlert(
  alertId: string,
  window: AlertMuteRequest,
): Promise<AlertResponse> {
  return httpClient.post<AlertResponse>(
    `/alerts/${encodeURIComponent(alertId)}/mute`,
    window,
  );
}

/** POST /alerts/{id}/unmute — no body; reopens the alert. */
export async function unmuteAlert(alertId: string): Promise<AlertResponse> {
  return httpClient.post<AlertResponse>(
    `/alerts/${encodeURIComponent(alertId)}/unmute`,
  );
}
